import type { AbandonedCheckout } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export type RecoveryState = 'recovered' | 'expired' | 'open';

/**
 * The backend already folds a passed `expiresAt` into `status: 'expired'`, so the state follows
 * the status alone; recovered wins when the shopper came back.
 */
export function recoveryState(
  checkout: Pick<AbandonedCheckout, 'status' | 'recoveredOrderId'>,
): RecoveryState {
  if (checkout.status === 'completed' || checkout.recoveredOrderId) return 'recovered';
  if (checkout.status === 'expired') return 'expired';
  return 'open';
}

export type EmailSentState = 'sent' | 'not_sent' | 'template_off';

/**
 * The backend stamps `recoveryEmailSentAt` only when the email was queued, so a timestamp is
 * trusted as is. A checkout still waiting reads as "switched off" when the store's recovery email
 * is off; `templateEnabled` is null when the member cannot read the template settings.
 */
export function emailSentState(
  checkout: Pick<AbandonedCheckout, 'recoveryEmailSentAt'>,
  templateEnabled: boolean | null,
): EmailSentState {
  if (checkout.recoveryEmailSentAt) return 'sent';
  return templateEnabled === false ? 'template_off' : 'not_sent';
}

/**
 * Why an open checkout offers no send button, when the row says enough to tell: no address or
 * email to write to, or the store's recovery email switched off. Null for anything else.
 */
export function unsendableHint(
  checkout: Pick<
    AbandonedCheckout,
    'status' | 'recoveredOrderId' | 'canSendRecovery' | 'email' | 'hasShippingAddress'
  >,
  templateEnabled: boolean | null,
): 'noAddress' | 'templateOff' | null {
  if (checkout.canSendRecovery || recoveryState(checkout) !== 'open') return null;
  if (!checkout.hasShippingAddress || checkout.email === '') return 'noAddress';
  return templateEnabled === false ? 'templateOff' : null;
}

/** Every `meta.reason` send-recovery answers with on a 409. */
export const RECOVERY_REFUSALS = [
  'template_disabled',
  'expired',
  'completed',
  'incomplete',
  'awaiting_payment',
  'cancelled',
] as const;

export type RecoveryRefusal = (typeof RECOVERY_REFUSALS)[number];

const isRefusal = (value: unknown): value is RecoveryRefusal =>
  typeof value === 'string' && (RECOVERY_REFUSALS as readonly string[]).includes(value);

/**
 * The plain-language explanation of a 409 from send-recovery, picked by `meta.reason`; a 409
 * without a known reason gets the general one. Other errors return null for the generic message.
 */
export function recoveryErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null;
  const reason = error.meta.reason;
  return isRefusal(reason)
    ? `abandonedCheckouts:recovery.refused.${reason}`
    : 'abandonedCheckouts:recovery.conflict';
}
